import { Corner, Root, Viewport } from '@radix-ui/react-scroll-area'
import { cn } from '@seen/ui'
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type RefCallback,
  type RefObject,
  useLayoutEffect,
  useState,
} from 'react'
import { ScrollBar } from '#src/components/core/scroll-area/ScrollBar.tsx'
import { useScrollRestorationBase } from '#src/hooks/useScrollRestorationBase.ts'
import { SCROLLED_INTO_VIEW_SELECTOR } from '#src/utils/scrollIntoView.ts'

type RootProps = ComponentPropsWithoutRef<typeof Root>
type ViewportProps = ComponentProps<typeof Viewport>

export interface ScrollAreaProps extends RootProps {
  contentClassName?: ViewportProps['className']
  className?: RootProps['className']
  scrollElementRef?:
    | RefObject<HTMLDivElement | null>
    | RefCallback<HTMLDivElement>
  scrollRestorationId: string | null
  disableScrollRestoration?: boolean
  highlightScrollBar?: boolean
  // Scroll axis. Horizontal lays the viewport content out as a row (e.g. a
  // tab strip wider than its container). Scroll restoration only applies to
  // the vertical axis.
  orientation?: 'vertical' | 'horizontal'
}

export const ScrollArea = forwardRef<ElementRef<typeof Root>, ScrollAreaProps>(
  (
    {
      className,
      contentClassName,
      children,
      scrollElementRef,
      scrollRestorationId,
      disableScrollRestoration,
      highlightScrollBar,
      orientation = 'vertical',
      ...delegated
    },
    ref
  ) => {
    const [innerHighlightScrollBar, setInnerHighlightScrollBar] =
      useState(false)
    useScrollRestoration(
      scrollRestorationId,
      disableScrollRestoration,
      setInnerHighlightScrollBar
    )

    return (
      <Root
        ref={ref}
        className={cn(
          'relative overflow-hidden',
          'size-full print:size-auto', // We almost always want a ScrollArea to take up the full height of its parent so it prevents bugs to have this by default.
          className
        )}
        {...delegated}
      >
        <Viewport
          ref={scrollElementRef}
          data-scroll-restoration-id={scrollRestorationId} // Note: VirtualizedList depends on this being set as data-scroll-restoration-id.
          className={cn(
            'size-full print:size-auto',
            'rounded-[inherit]',
            'isolate', // Prevent other elements from appearing over the scrollbar.
            // The important modifier (!) is needed to overcome the specificity coming from Radix which notoriously breaks the UI in certain cases by allowing children to escape the container.
            // https://github.com/radix-ui/primitives/issues/2722
            // https://github.com/radix-ui/primitives/issues/926
            orientation === 'horizontal'
              ? '[&>div]:flex! [&>div]:flex-row! [&>div]:!print:block'
              : '[&>div]:flex! [&>div]:flex-col! [&>div]:!print:block',
            contentClassName
          )}
        >
          {children}
        </Viewport>
        <ScrollBar
          orientation={orientation}
          className="print:hidden"
          forceMount={
            innerHighlightScrollBar || highlightScrollBar ? true : undefined
          }
          highlight={innerHighlightScrollBar || highlightScrollBar}
        />
        <Corner className="print:hidden" />
      </Root>
    )
  }
)
ScrollArea.displayName = Root.displayName

function useScrollRestoration(
  id: string | undefined | null,
  disableScrollRestoration?: boolean,
  setHighlightScrollBar?: (highlightScrollBar: boolean) => void
): void {
  const { storage, locationKey, elementSelector } = useScrollRestorationBase(
    id,
    disableScrollRestoration
  )

  // We use useLayoutEffect to restore the scroll position as soon as possible.
  useLayoutEffect(() => {
    if (!elementSelector || !storage || disableScrollRestoration) {
      return
    }

    const element = document.querySelector(elementSelector)
    if (!element) {
      return
    }

    // If a child has scrolled into view, don't restore the scroll position.
    if (element.querySelector(SCROLLED_INTO_VIEW_SELECTOR)) {
      return
    }

    const stored = storage.get()?.[locationKey]
    if (!stored) {
      return
    }

    setHighlightScrollBar?.(true)
    const scrollBarTimeoutId = setTimeout(() => {
      setHighlightScrollBar?.(false)
    }, 1000)

    element.scrollTop = stored

    const ac = new AbortController()

    element.addEventListener(
      'scroll',
      () => {
        // If a child has scrolled into view, don't restore the scroll position.
        if (element.querySelector(SCROLLED_INTO_VIEW_SELECTOR)) {
          ac.abort()
          return
        }
        element.scrollTop = stored
      },
      {
        signal: ac.signal,
        passive: true,
      }
    )

    element.addEventListener('wheel', () => ac.abort(), {
      once: true,
      signal: ac.signal,
      passive: true,
    })
    element.addEventListener('touchstart', () => ac.abort(), {
      once: true,
      signal: ac.signal,
      passive: true,
    })

    const abortTimeoutId = setTimeout(() => {
      ac.abort()
    }, 3000)

    return () => {
      ac.abort()
      clearTimeout(abortTimeoutId)
      clearTimeout(scrollBarTimeoutId)
    }
  }, [
    elementSelector,
    locationKey,
    storage,
    disableScrollRestoration,
    setHighlightScrollBar,
  ])
}
